// src/features/monetize/components/CompDrawer.tsx

import { useEffect } from 'react';
import { Button, Drawer, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useController, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { DateTimeField } from '@/shared/components/DateTimeField';
import { COMP_MODES, COMP_MODE_DESCRIPTIONS, MONETIZE_CONSTRAINTS } from '@/lib/constants/monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { compFormSchema, type CompFormValues } from '../schemas/monetizeSchemas';
import { useComp } from '../useMonetizeMutations';
import { ScopePicker } from './ScopePicker';

interface CompDrawerProps {
  opened: boolean;
  viewerId: string;
  viewerEmail: string;
  onClose: () => void;
}

/** Grant a viewer free access: to playlists or items, or to everything, with an optional end. */
export function CompDrawer({ opened, viewerId, viewerEmail, onClose }: CompDrawerProps) {
  const { selectedCId } = useAuth();
  const comp = useComp();
  const form = useForm<CompFormValues>({ resolver: zodResolver(compFormSchema), defaultValues: MonetizeUtils.defaultCompForm() });

  useEffect(() => {
    if (opened) form.reset(MonetizeUtils.defaultCompForm());
  }, [opened, form]);

  const wholeService = useWatch({ control: form.control, name: 'wholeService' });
  const scopes = useController({ control: form.control, name: 'scopes' });
  const wholeServiceField = useController({ control: form.control, name: 'wholeService' });
  const accessMode = useController({ control: form.control, name: 'accessMode' });
  const endsAt = useController({ control: form.control, name: 'endsAt' });
  const reason = useController({ control: form.control, name: 'reason' });

  async function handleSubmit(values: CompFormValues) {
    if (selectedCId == null) return;
    const { scopes: tokens, req } = MonetizeUtils.formToComp(values, selectedCId);
    try {
      const rows = await comp.mutateAsync({ viewerId, scopes: tokens, req });
      notifications.show({ title: 'Access granted', message: `${rows.length === 1 ? 'One access row' : `${rows.length} access rows`} added for ${viewerEmail}.`, color: 'green' });
      onClose();
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not grant access.'), color: 'red' });
    }
  }

  return (
    <Drawer opened={opened} onClose={onClose} title={`Grant access to ${viewerEmail}`} position="right" size="md" padding="lg" closeOnClickOutside={!comp.isPending} closeOnEscape={!comp.isPending}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">Free access, no payment. The viewer sees it the next time they play.</Text>
          <ScopePicker
            value={scopes.field.value}
            onChange={scopes.field.onChange}
            wholeService={wholeService}
            onWholeServiceChange={wholeServiceField.field.onChange}
            error={scopes.fieldState.error?.message}
          />
          <Select
            label="Shown as"
            description="How the access appears in reports; a comp is the usual choice"
            data={COMP_MODES.map((m) => ({ value: m, label: COMP_MODE_DESCRIPTIONS[m] }))}
            value={accessMode.field.value}
            onChange={(v) => v && accessMode.field.onChange(v)}
            allowDeselect={false}
          />
          <DateTimeField label="Ends" description="Empty = no end" clearable valueFormat="MMM D, YYYY h:mm A" value={endsAt.field.value} onChange={(v) => endsAt.field.onChange(v || null)} error={endsAt.fieldState.error?.message} />
          <TextInput
            label="Reason"
            withAsterisk
            description="For your records; it appears in the viewer's history"
            placeholder="Season ticket holder"
            maxLength={MONETIZE_CONSTRAINTS.REASON_MAX}
            value={reason.field.value}
            onChange={reason.field.onChange}
            onBlur={reason.field.onBlur}
            error={reason.fieldState.error?.message}
          />
          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={comp.isPending}>Grant access</Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
